import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  DocumentArrowDownIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { resultadosService, type Resultado } from '../services/resultados.service';
import DataTable from '../components/DataTable/DataTable';
import type { Column } from '../types/data-table';
import { useToast, ToastContainer } from '../components/Toast';
import { PageHeader } from '../components/PageHeader';
import { formatDate } from '../utils/dateUtils';
import { useEmpresa } from '../contexts/EmpresaContext';

export default function Resultados() {
  const { empresa } = useEmpresa();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');

  const { data: response, isLoading, error } = useQuery({
    queryKey: ['resultados', empresa?.id, page, limit, search],
    queryFn: () => resultadosService.getAllResultados(page, limit, search),
    placeholderData: keepPreviousData,
    enabled: !!empresa?.id,
  });

  const resultados = response?.data || [];
  const meta = response?.meta || { total: 0, page: 1, limit: 10, totalPages: 0 };

  const handleDownload = async (resultado: Resultado) => {
    try {
      const blob = await resultadosService.downloadResultado(resultado.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resultado_${resultado.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Download iniciado');
    } catch (error) {
      console.error('❌ Erro ao fazer download:', error);
      toast.error('Erro ao fazer download do arquivo. Tente novamente.');
    }
  };

  const columns: Column<Resultado>[] = [
    {
      key: 'exame',
      label: 'Exame',
      sortable: true,
      render: (item) => (
        <div>
          <div className="font-medium text-gray-900">{item.exame}</div>
          <div className="text-xs text-gray-500">{item.clinica}</div>
        </div>
      ),
    },
    {
      key: 'colaborador',
      label: 'Colaborador',
      sortable: true,
      render: (item) => (
        <div>
          <div className="font-medium text-gray-900">{item.colaborador.nome}</div>
          <div className="text-xs text-gray-500">CPF: {item.colaborador.cpf}</div>
        </div>
      ),
    },
    {
      key: 'data_realizacao',
      label: 'Realizado em',
      sortable: true,
      render: (item) => formatDate(item.data_realizacao),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (item) => {
        const getStatusBadge = (status: string) => {
          switch (status) {
            case 'aprovado':
              return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <CheckCircleIcon className="w-3 h-3 mr-1" />
                  Aprovado
                </span>
              );
            case 'reprovado':
              return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <ExclamationCircleIcon className="w-3 h-3 mr-1" />
                  Reprovado
                </span>
              );
            default:
              return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  <ClockIcon className="w-3 h-3 mr-1" />
                  Pendente
                </span>
              );
          }
        };
        return getStatusBadge(item.status);
      },
    },
    {
      key: 'valor',
      label: 'Resultado',
      sortable: true,
      render: (item) => (
        <span className="text-sm text-gray-700 truncate max-w-xs block" title={item.valor || ''}>
          {item.valor || '-'}
        </span>
      ),
    },
  ];

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-red-800 font-medium">Erro ao carregar resultados</h3>
        <p className="text-red-600 text-sm mt-1">
          Não foi possível carregar a lista de resultados. Tente novamente mais tarde.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Resultados de Exames"
        description="Visualize e baixe os resultados dos exames dos seus colaboradores"
      />

      <DataTable
        data={resultados}
        columns={columns}
        loading={isLoading}
        getId={(item) => item.id}
        pagination={{
          page: meta.page,
          limit: meta.limit,
          total: meta.total,
          totalPages: meta.totalPages,
        }}
        onPageChange={setPage}
        onLimitChange={setLimit}
        onFiltersChange={(filters) => setSearch(filters.search)}
        actions={(item) => (
          <button
            onClick={() => handleDownload(item)}
            className="text-primary-600 hover:text-primary-800 transition-colors p-1"
            title="Baixar Resultado"
          >
            <DocumentArrowDownIcon className="w-5 h-5" />
          </button>
        )}
        emptyMessage="Nenhum resultado encontrado."
      />


      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
    </div>
  );
}
